import React from "react";

import { css, cx } from "@emotion/css";

import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { formatNumber } from "@/utils/mathUtils.ts";

export type InsulinStatItemProps = {
  title: string;
  percentage?: number;
  percentagePrecision?: number;
  value: number | string;
  precision?: number;
  unit?: string;
  className?: string;
  legend?: React.ReactElement;
};

export const InsulinStatItem: React.FC<InsulinStatItemProps> = ({
  title,
  value,
  percentage,
  percentagePrecision = 2,
  precision = 2,
  unit,
  legend,
  className,
}) => {
  const styles = useStyles(makeStyles);

  const titleElement = (
    <div className={styles.titleContainer}>
      {legend}
      <Typography variant="body" className={styles.title}>
        {title}
      </Typography>
    </div>
  );

  if (percentage !== undefined) {
    return (
      <div className={cx(styles.container, className)}>
        {titleElement}
        <div className={styles.values}>
          <Typography variant="subtitleLarge" className={styles.value}>
            {typeof value == "number" ? formatNumber(value, precision) : value}
          </Typography>
          <Typography variant="body">{` ${unit ?? ""}`}</Typography>
          <Typography variant="bodySmall" className={cx(styles.value, styles.percentage)}>
            {`${formatNumber(percentage * 100, percentagePrecision)}%`}
          </Typography>
        </div>
      </div>
    );
  } else {
    return (
      <div className={cx(styles.container, className)}>
        {titleElement}
        <div className={styles.values}>
          <Typography variant="subtitleLarge" className={styles.value}>
            {typeof value == "number" ? formatNumber(value, precision) : value}
          </Typography>
          <Typography variant="body">{` ${unit ?? ""}`}</Typography>
        </div>
      </div>
    );
  }
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: column;
  `,
  titleContainer: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${theme.spacing(2)};
  `,
  title: css`
    flex: 1 1;
    white-space: nowrap;
    overflow: clip;
    text-overflow: ellipsis;
  `,
  values: css`
    display: flex;
    flex-direction: row;
    align-items: baseline;
    gap: ${theme.spacing(2)};
  `,
  value: css`
    margin-top: ${theme.spacing(4)};
    margin-bottom: ${theme.spacing(2)};
  `,
  percentage: css`
    margin-left: ${theme.spacing(2)};
  `,
});
